import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

const submitAddr = 'https://readdy.ai/api/form/d9u04sdca23r0tr1anrg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系！');
      form.reset();
      return;
    }
    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') {
          params.append(key, value);
        }
      });
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '亚洲综合网是什么样的平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '亚洲综合网是专注亚洲地区的综合性信息平台，汇聚亚洲娱乐、财经、科技、生活等全品类内容，为用户提供最新、最全、最权威的亚洲资讯服务，一站式满足您对亚洲各领域信息的需求。',
        },
      },
      {
        '@type': 'Question',
        name: '亚洲综合网的内容覆盖哪些领域？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '亚洲综合网的内容覆盖娱乐、财经、科技和生活四大核心领域。娱乐频道涵盖K-POP、宝莱坞、日本动漫、亚洲电竞等；财经频道聚焦亚洲金融市场、数字货币、私募股权等；科技频道关注AI、半导体、6G等前沿技术；生活频道则提供旅游、美食、健康等丰富内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系亚洲综合网？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面底部的联系表单与我们取得联系，也可以发送邮件至 contact@985huidian.cn。我们会在1-3个工作日内回复您的咨询。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们"
        description="亚洲综合网是专注亚洲地区的综合性信息平台，了解我们的品牌故事、核心价值与联系方式。汇聚亚洲娱乐、财经、科技、生活等全品类内容。"
        keywords="关于亚洲综合网,亚洲综合网品牌,亚洲资讯平台介绍,联系我们"
        canonicalUrl="https://985huidian.cn/about"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={{
        name: '关于我们 - 亚洲综合网',
        url: 'https://985huidian.cn/about',
      }} />
      <SchemaOrg type="FAQPage" data={faqSchema.mainEntity ? { mainEntity: faqSchema.mainEntity } : {}} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <main>
          {/* Brand Introduction */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-12">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mb-4">
                关于亚洲综合网
              </h1>
              <p className="text-foreground-600 text-lg leading-relaxed">
                汇聚亚洲精彩，传递资讯价值
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
              <div>
                <h2 className="font-heading font-bold text-2xl text-foreground-950 mb-5">品牌故事</h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed text-sm">
                  <p>
                    亚洲综合网创立于2024年，总部位于中国北京，是一家专注于亚洲地区的综合性信息平台。我们的使命是汇聚亚洲娱乐、财经、科技、生活等全品类内容，为全球华人用户提供最新、最全、最权威的亚洲资讯服务。
                  </p>
                  <p>
                    在这个信息爆炸的时代，亚洲综合网坚持"真实、深度、全面"的编辑理念，拥有一支分布在中国、日本、韩国、新加坡、印度等多个亚洲国家的专业采编团队，确保每一篇报道都能为读者带来最有价值的信息和洞察。
                  </p>
                  <p>
                    自上线以来，亚洲综合网已累计发布超过5万篇原创内容，月活跃用户突破500万，覆盖全球超过30个国家和地区，已成为亚洲地区最具影响力的中文综合资讯平台之一。
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20open%20newsroom%20office%20with%20diverse%20Asian%20team%20collaborating%20on%20large%20digital%20screens%20displaying%20data%20and%20news%20warm%20natural%20lighting%20professional%20corporate%20photography%20with%20clean%20minimalist%20design&width=700&height=500&seq=about-brand-01&orientation=landscape"
                  alt="亚洲综合网团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Core Values */}
            <div className="mb-20">
              <h2 className="font-heading font-bold text-2xl text-foreground-950 text-center mb-10">核心价值</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: 'ri-shield-check-line', title: '权威可信', desc: '严格的事实核查流程，确保每一条资讯的真实性和可靠性，成为用户信赖的信息来源。' },
                  { icon: 'ri-speed-up-line', title: '及时高效', desc: '7×24小时全天候内容更新，第一时间传递亚洲各地重要事件和热点资讯。' },
                  { icon: 'ri-global-line', title: '覆盖全面', desc: '涵盖亚洲30+国家和地区，横跨娱乐、财经、科技、生活四大核心领域。' },
                  { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户需求为导向，持续优化阅读体验，提供个性化内容推荐服务。' },
                ].map((item) => (
                  <div key={item.title} className="rounded-xl bg-background-100 p-6 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-2xl`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="mb-20">
              <div className="rounded-2xl bg-primary-500 p-10 md:p-14">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                  {[
                    { num: '5万+', label: '原创内容' },
                    { num: '500万+', label: '月活用户' },
                    { num: '30+', label: '覆盖国家' },
                    { num: '99.8%', label: '内容准确率' },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <p className="font-heading font-bold text-3xl md:text-4xl text-background-50 mb-2">{stat.num}</p>
                      <p className="text-background-200 text-sm">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Reputation */}
            <div className="mb-20">
              <h2 className="font-heading font-bold text-2xl text-foreground-950 text-center mb-10">行业声誉</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { title: '2025年度最佳中文资讯平台', org: '亚洲数字媒体协会', desc: '凭借优质的内容质量和卓越的用户体验，荣获亚洲数字媒体协会颁发的年度最佳中文资讯平台大奖。' },
                  { title: '内容安全认证AAA级', org: '中国互联网协会', desc: '通过中国互联网协会内容安全评估，获得最高AAA级认证，平台内容管理达到行业领先水平。' },
                  { title: '最具影响力新媒体', org: '世界华文媒体联盟', desc: '被世界华文媒体联盟评为2025年度最具影响力中文新媒体，内容传播力覆盖全球华人社群。' },
                ].map((item) => (
                  <div key={item.title} className="rounded-xl bg-background-100 p-6 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-award-line text-xl"></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-xs text-secondary-600 font-medium mb-3">{item.org}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="font-heading font-bold text-2xl text-foreground-950 mb-5">联系我们</h2>
                <div className="space-y-4 text-sm text-foreground-700">
                  <p className="leading-relaxed">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们联系。我们的客服团队将在1-3个工作日内为您提供回复。
                  </p>
                  <div className="space-y-3 mt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <p className="font-medium text-foreground-950">contact@985huidian.cn</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">联系电话</p>
                        <p className="font-medium text-foreground-950">+86-10-8888-6666</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">公司地址</p>
                        <p className="font-medium text-foreground-950">北京市朝阳区建国路88号亚洲传媒大厦18层</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="rounded-xl bg-background-100 p-6">
                  <h3 className="font-heading font-semibold text-foreground-950 mb-5">发送留言</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                          placeholder="请输入留言主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>
                      <button
                        type="submit"
                        className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        发送留言
                      </button>
                    </div>
                  </form>
                  {formStatus !== 'idle' && (
                    <div className={`mt-4 p-3 rounded-lg text-sm ${
                      formStatus === 'success'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {formMessage}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}